import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, RefreshCw, X, Twitter, Facebook, Linkedin, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchSocialKit, generateSocialKit, socialKitImageUrl } from "@/lib/api";

const CHANNELS = [
  { key: "x", label: "X / Twitter", icon: Twitter },
  { key: "facebook", label: "Facebook", icon: Facebook },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin },
];

/**
 * Admin-only modal that shows three ready-to-paste captions and one social
 * share image for a specific ebook. Captions + image are cached per ebook
 * so re-opening the modal doesn't cost LLM credits; the Regenerate button
 * forces a fresh set.
 */
export default function SocialKitModal({ ebook, onClose }) {
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const existing = await fetchSocialKit(ebook.id);
        if (!cancelled) setKit(existing);
      } catch {
        if (!cancelled) setKit(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ebook.id]);

  const generate = async () => {
    try {
      setBusy(true);
      const res = await generateSocialKit(ebook.id);
      setKit(res);
      toast.success("Social kit ready — captions and image generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed — try again in a moment");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const imageUrl = kit?.image_url
    ? socialKitImageUrl(ebook.id)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-obsidian-950/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="admin-social-kit-modal"
    >
      <div className="relative bg-obsidian-800 border border-obsidian-600 max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-bone-500 hover:text-bone-200"
          data-testid="admin-social-kit-close"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-2">
          Social post kit
        </p>
        <h2 className="font-serif text-2xl text-bone-100 mb-1">{ebook.title}</h2>
        <p className="font-serif italic text-bone-400 text-sm mb-6">
          Three ready-to-paste captions and one editorial share image. Posts land quickly, without staring at a blank field.
        </p>

        {loading ? (
          <div className="flex items-center gap-3 text-bone-400 py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> loading kit
          </div>
        ) : !kit ? (
          <div
            className="border border-obsidian-600 bg-obsidian-900/50 p-6 flex flex-col items-start gap-4"
            data-testid="admin-social-kit-empty"
          >
            <p className="font-serif italic text-bone-300">
              No kit yet — Claude will draft the captions and Nano Banana will paint the image.
              This uses your Emergent LLM key and takes about 20 seconds.
            </p>
            <Button
              onClick={generate}
              disabled={busy}
              data-testid="admin-social-kit-generate"
              className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-6 py-5"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Generate the kit
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {imageUrl && (
              <div className="border border-obsidian-600 bg-obsidian-900/40 p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-bone-500">
                    Share image · {kit.model_image || "Nano Banana"}
                  </p>
                  <a
                    href={imageUrl}
                    download={`${(ebook.title || "ebook").replace(/\s+/g, "_")}_share.png`}
                    className="inline-flex items-center gap-2 font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta hover:underline"
                    data-testid="admin-social-kit-download-image"
                  >
                    <Download className="h-3 w-3" /> Download
                  </a>
                </div>
                <img
                  src={imageUrl}
                  alt={`Social share image for ${ebook.title}`}
                  className="w-full aspect-square object-cover border border-obsidian-600"
                  data-testid="admin-social-kit-image"
                />
              </div>
            )}

            {CHANNELS.map(({ key, label, icon: Icon }) => (
              <div
                key={key}
                className="border border-obsidian-600 bg-obsidian-900/40 p-4"
                data-testid={`admin-social-kit-caption-${key}`}
              >
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-bone-500 inline-flex items-center gap-2">
                    <Icon className="h-3 w-3" aria-hidden="true" /> {label}
                  </p>
                  <button
                    onClick={() => copy(kit.captions?.[key] || "", label)}
                    className="inline-flex items-center gap-2 font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta hover:underline"
                    data-testid={`admin-social-kit-copy-${key}`}
                  >
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
                <p className="font-serif text-bone-200 text-[1rem] leading-relaxed whitespace-pre-wrap">
                  {kit.captions?.[key] || "(empty)"}
                </p>
              </div>
            ))}

            <div className="flex items-center gap-3 flex-wrap pt-2">
              <Button
                onClick={generate}
                disabled={busy}
                data-testid="admin-social-kit-regenerate"
                variant="outline"
                className="rounded-none border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian-900 font-sans uppercase tracking-wider text-xs px-6 py-4 bg-transparent"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Regenerate
              </Button>
              {kit.generated_at && (
                <p className="font-sans text-[0.6rem] text-bone-500">
                  Generated {new Date(kit.generated_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
